import React from "react";

import MessageCircleIcon from "@uui/MessageCircle02";

import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import styled from "@mui/material/styles/styled";

import { Column, Row, StackProps } from "@rui/Stack";

const ProposedAnswerWrapper = styled(Row)(({ theme }) => ({
  backgroundColor: theme.palette.shades.secondary[50],
  padding: theme.spacing(0.5, 1, 0.5, 0.5),
  borderRadius: "4px",
  minHeight: "34px",
  "&.editable": {
    "&:hover": {
      backgroundColor: theme.palette.shades.secondary[100],
    },
  },
  "& .key-question-answer__text": {
    ...theme.typography.detailReg,
    lineHeight: 19 / 14,
    paddingTop: "2px",
  },
}));

export const Answer = styled(Row)(({ theme }) => ({
  position: "relative",
  alignItems: "center",
  backgroundColor: theme.palette.transparent.black[2],
  color: theme.palette.shades.neutral[600],
  padding: theme.spacing(1),
  borderRadius: "0px, 4px, 4px, 0px",
  borderLeft: "2px solid",
  borderColor: theme.palette.shades.neutral[150],
  "&.editable": {
    minHeight: "40px",
    "&:hover": {
      backgroundColor: theme.palette.transparent.black[4],
      "& .key-question-answer__edit-answer": {
        display: "flex",
      },
    },
  },
  "& .key-question-answer__edit-answer": {
    display: "none",
  },

  "& .key-question-answer__text": {
    ...theme.typography.detailReg,
    lineHeight: 19 / 14,
  },
}));

export const ProposedAnswer = styled<React.FC<StackProps>>(
  ({ className, ...props }) => (
    <ProposedAnswerWrapper className={className}>
      <Row variant="left" className="proposed_answer__inner" {...props} />
    </ProposedAnswerWrapper>
  )
)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  ".proposed_answer__inner::before": {
    content: '""',
    backgroundColor: theme.palette.shades.secondary[200],
    minWidth: "2px",
    height: "100%",
    minHeight: "100%",
    borderRadius: "8px",
    marginRight: theme.spacing(0.5),
  },
}));

export const RejectedProposedAnswer = styled<React.FC<StackProps>>((props) => (
  <ProposedAnswerWrapper>
    <Row variant="left" {...props} />
  </ProposedAnswerWrapper>
))(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  "&::before": {
    content: '""',
    backgroundColor: theme.palette.shades.secondary[200],
    width: "2px",
    height: "100%",
    minHeight: "100%",
    borderRadius: "8px",
    marginRight: theme.spacing(0.5),
  },
}));

export const AnswerButton = styled<React.FC<LoadingButtonProps>>((props) => (
  <LoadingButton variant="outlined" {...props} />
))(({ theme }) => ({
  position: "absolute",
  right: 4,
  top: 4,
  backgroundColor: "#FFF",
  borderColor: theme.palette.shades.neutral[100],
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.75rem",
  padding: theme.spacing(1, 1.5),
  borderRadius: "4px",
  minWidth: "fit-content",
  "&:hover": {
    backgroundColor: "#FFF",
  },
}));

type TextFieldWrapperProps = StackProps & {
  marginRight?: React.CSSProperties["marginRight"];
};

export const TextFieldWrapper = styled<React.FC<TextFieldWrapperProps>>(
  ({ marginRight: _mr, ...props }) => <Column {...props} />
)(({ marginRight, theme }) => ({
  position: "relative",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.25, 1),
    fontFamily: theme.typography.fontFamilySecondary,
    marginRight,
    fontSize: "0.875rem",
  },
}));

export const ActionButton = styled(LoadingButton)(({ theme }) => ({
  color: theme.palette.shades.neutral[400],
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.75rem",
  padding: theme.spacing(0.75, 1),
  minWidth: "fit-content",
  "&:hover": {
    color: theme.palette.shades.primary[500],
  },
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: theme.spacing(0.5),
  },
  "& .MuiSvgIcon-root": {
    fontSize: "14px !important",
  },
}));

export const GeneratedByRead = styled<React.FC<StackProps>>((props) => (
  <Row variant="center" spacing={0.5} {...props}>
    <MessageCircleIcon />
    <span>Generated by Read</span>
  </Row>
))(({ theme }) => ({
  fontSize: "0.75rem",
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[400],

  "& .MuiSvgIcon-root": {
    color: theme.palette.shades.neutral[200],
    fontSize: "inherit",
  },
}));
